import {
    ApiSpinTogetherEventsReqV1Schema,
    ApiSpinTogetherEventsReqV2Schema,
    ApiSpinTogetherEventsResV1Schema,
    ApiSpinTogetherEventsResV2Schema,
    ApiSpinTogetherRealtimeTicketReqV1Schema,
    ApiSpinTogetherRealtimeTicketReqV2Schema,
    ApiSpinTogetherRealtimeTicketResV1Schema,
    ApiSpinTogetherRealtimeTicketResV2Schema,
    ApiSpinTogetherSnapshotReqV1Schema,
    ApiSpinTogetherSnapshotReqV2Schema,
    ApiSpinTogetherSnapshotResV1Schema,
    ApiSpinTogetherSnapshotResV2Schema,
    SpinTogetherRealtimeEventV1Schema,
    SpinTogetherRealtimeEventV2Schema,
    type ApiSpinTogetherDisplayConfigAny,
    type ApiSpinTogetherFriendStateAny,
    type ApiSpinTogetherInvite,
    type ApiSpinTogetherPreference,
    type ApiSpinTogetherRealtimeTicketReqV1,
    type ApiSpinTogetherRealtimeTicketReqV2,
    type ApiSpinTogetherSnapshotResV1,
    type ApiSpinTogetherSnapshotResV2,
} from "@bridge-applications/pb-types";

const SPIN_TOGETHER_EVENTS_LIMIT = 200;

type SpinTogetherSnapshotOkV1 = Extract<
    ApiSpinTogetherSnapshotResV1,
    { status: "OK" }
>;
type SpinTogetherSnapshotOkV2 = Extract<
    ApiSpinTogetherSnapshotResV2,
    { status: "OK" }
>;

export interface SpinTogetherNormalizedSnapshot {
    preference: ApiSpinTogetherPreference;
    config: ApiSpinTogetherDisplayConfigAny | null;
    friends: ApiSpinTogetherFriendStateAny[];
    invites: ApiSpinTogetherInvite[];
    lastSequence: string;
    observedAt: string;
}

export function spinTogetherV2SnapshotRequiresV1Fallback(
    response: ApiSpinTogetherSnapshotResV2,
): boolean {
    return (
        response.status === "ERROR" && response.code === "FEATURE_UNAVAILABLE"
    );
}

export function spinTogetherV1SnapshotRequiresV2Retry(
    response: ApiSpinTogetherSnapshotResV1,
): boolean {
    return (
        response.status === "ERROR" && response.code === "FEATURE_UNAVAILABLE"
    );
}

function queryPath(
    pathname: string,
    entries: ReadonlyArray<readonly [string, string]>,
): string {
    return `${pathname}?${new URLSearchParams(
        entries.map(([key, value]) => [key, value]),
    ).toString()}`;
}

export const SPIN_TOGETHER_TRANSPORT_V1 = {
    version: 1 as const,
    snapshotResponseSchema: ApiSpinTogetherSnapshotResV1Schema,
    eventsResponseSchema: ApiSpinTogetherEventsResV1Schema,
    realtimeTicketResponseSchema: ApiSpinTogetherRealtimeTicketResV1Schema,
    realtimeEventSchema: SpinTogetherRealtimeEventV1Schema,
    snapshotPath(): string {
        ApiSpinTogetherSnapshotReqV1Schema.parse({});
        return "/api/v1/spin-together/snapshot";
    },
    eventsPath(afterSequence: string): string {
        const request = ApiSpinTogetherEventsReqV1Schema.parse({
            afterSequence,
            limit: SPIN_TOGETHER_EVENTS_LIMIT,
        });
        return queryPath("/api/v1/spin-together/events", [
            ["afterSequence", request.afterSequence],
            ["limit", String(request.limit)],
        ]);
    },
    realtimeTicketPath: "/api/v1/spin-together/realtime-ticket",
    realtimeTicketRequest(
        resumeAfterSequence: string,
    ): ApiSpinTogetherRealtimeTicketReqV1 {
        return ApiSpinTogetherRealtimeTicketReqV1Schema.parse({
            resumeAfterSequence,
        });
    },
    normalizeSnapshot(
        snapshot: SpinTogetherSnapshotOkV1,
    ): SpinTogetherNormalizedSnapshot {
        return {
            preference: snapshot.preference,
            config: snapshot.config,
            friends: snapshot.friends,
            invites: snapshot.invites,
            lastSequence: snapshot.realtimeCursor,
            observedAt: snapshot.observedAt,
        };
    },
};

export const SPIN_TOGETHER_TRANSPORT_V2 = {
    version: 2 as const,
    snapshotResponseSchema: ApiSpinTogetherSnapshotResV2Schema,
    eventsResponseSchema: ApiSpinTogetherEventsResV2Schema,
    realtimeTicketResponseSchema: ApiSpinTogetherRealtimeTicketResV2Schema,
    realtimeEventSchema: SpinTogetherRealtimeEventV2Schema,
    snapshotPath(): string {
        const request = ApiSpinTogetherSnapshotReqV2Schema.parse({
            apiSchemaVersion: 2,
        });
        return queryPath("/api/v2/spin-together/snapshot", [
            ["apiSchemaVersion", String(request.apiSchemaVersion)],
        ]);
    },
    eventsPath(afterSequence: string): string {
        const request = ApiSpinTogetherEventsReqV2Schema.parse({
            realtimeSchemaVersion: 2,
            afterSequence,
            limit: SPIN_TOGETHER_EVENTS_LIMIT,
        });
        return queryPath("/api/v2/spin-together/events", [
            ["realtimeSchemaVersion", String(request.realtimeSchemaVersion)],
            ["afterSequence", request.afterSequence],
            ["limit", String(request.limit)],
        ]);
    },
    realtimeTicketPath: "/api/v2/spin-together/realtime-ticket",
    realtimeTicketRequest(
        resumeAfterSequence: string,
    ): ApiSpinTogetherRealtimeTicketReqV2 {
        return ApiSpinTogetherRealtimeTicketReqV2Schema.parse({
            requestedRealtimeSchemaVersion: 2,
            resumeAfterSequence,
        });
    },
    normalizeSnapshot(
        snapshot: SpinTogetherSnapshotOkV2,
    ): SpinTogetherNormalizedSnapshot {
        return {
            preference: snapshot.preference,
            config: snapshot.config,
            friends: snapshot.friends,
            invites: snapshot.invites,
            lastSequence: snapshot.lastSequence,
            observedAt: snapshot.observedAt,
        };
    },
};

export type SpinTogetherTransport =
    typeof SPIN_TOGETHER_TRANSPORT_V1 | typeof SPIN_TOGETHER_TRANSPORT_V2;

export function spinTogetherTransportRequiresReconnect(
    current: SpinTogetherTransport | null,
    next: SpinTogetherTransport,
): boolean {
    return current !== null && current.version !== next.version;
}

export function spinTogetherTransportSupportsPeriodicV2Refresh(
    current: SpinTogetherTransport | null,
): boolean {
    return current !== null;
}

export interface SpinTogetherSnapshotSequenceReconciliation {
    committedSequence: string;
    preserveCurrentRealtimeState: boolean;
}

export function reconcileSpinTogetherSnapshotSequence(
    requestStartSequence: string,
    currentSequence: string,
    snapshotSequence: string,
    sameRealtimeDomain: boolean,
): SpinTogetherSnapshotSequenceReconciliation {
    if (!sameRealtimeDomain) {
        return {
            committedSequence: snapshotSequence,
            preserveCurrentRealtimeState: false,
        };
    }
    const latestLocalSequence = [requestStartSequence, currentSequence].reduce(
        (latest, candidate) =>
            BigInt(candidate) > BigInt(latest) ? candidate : latest,
    );
    if (BigInt(latestLocalSequence) > BigInt(snapshotSequence)) {
        return {
            committedSequence: latestLocalSequence,
            preserveCurrentRealtimeState: true,
        };
    }
    return {
        committedSequence: snapshotSequence,
        preserveCurrentRealtimeState: false,
    };
}

export function reconcileSpinTogetherSnapshotRealtimeState<TState>(
    currentState: TState,
    snapshotState: TState,
    preserveCurrentRealtimeState: boolean,
): TState {
    return preserveCurrentRealtimeState ? currentState : snapshotState;
}
